import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to use React Context effectively",
  "description": "",
  "published": "2019-01-02T00:00:00.000Z",
  "redirect_from": ["/blog/react-context-effectively/swizec/8844"],
  "categories": "Front End, Technical",
  "hero": "./img/profile_images-834049142515187713-cOtVTgLm_400x400.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Modern React Context has been with us for a while but I still see a lot of confusion about how to use it effectively.`}</p>
    <p>{`A lot of people feel that no matter what, you still need `}<em parentName="p">{`some`}</em>{` form of state management. Either an existing library or you end up building your own anyway.`}</p>
    <p>{`Yes, sort of. But not in the way you think.`}</p>
    <p><a parentName="p" {...{
        "href": "https://redux.js.org/"
      }}>{`Redux`}</a>{` and `}<a parentName="p" {...{
        "href": "https://github.com/mobxjs/mobx"
      }}>{`MobX`}</a>{` have always used context behind the scenes. They're familiar, you can use them right away, and your workflow doesn't have to change. Congratz, you're using React Context effectively.`}</p>
    <p>{`Maybe you don't like or need the complexity of Redux and MobX. Overhead, bundle sizes, indirection and separation of concerns way beyond what your tiny app needs. Lots of reasons.`}</p>
    <p>{`That's where modern React Context comes in.`}</p>
    <h2 {...{
      "id": "work-with-context-directly"
    }}>{`Work with context directly`}</h2>
    <p>{`Here's a video of mine explaining React Context in 2 minutes 👇`}</p>
    <lite-youtube {...{
      "videoid": "2WXUyMJmF2Q",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`To use context effectively you need 3 things:`}</p>
    <ol>
      <li parentName="ol">{`A state object`}</li>
      <li parentName="ol">{`A way to change state from consumers`}</li>
      <li parentName="ol">{`A way to communicate state changes to your provider`}</li>
    </ol>
    <p>{`That is all.`}</p>
    <p>{`A state object is where you hold your state tree. Many ways to build one, I prefer plain JavaScript objects. A reducer-like approach is also popular.`}</p>
    <p>{`Then you need some way to change said state. I like to use functions attached to the state object itself. That way anyone who has access to state also has the ability to change values. Functions are best. Think of them as actions.`}</p>
    <p>{`Finally, you need a way for your Provider to know that state changed. This is important because React uses the same props-changed tree diffing algorithm to update context as it does for reconciliation and re-rendering.`}</p>
    <p>{`JavaScript uses shallow object comparisons, so relying on component state is best. React comes with all necessary machinery built-in.`}</p>
    <p>{`Here's a bug you can run into if you're not careful:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`let obj = {
  pugson: {
    greet: "Hello",
  },
}

let obj2 = { ...obj }

obj2 == obj // false
obj2.pugson == obj.pugson // true
`}</code></pre>
    <p>{`Even though we made a copy of `}<inlineCode parentName="p">{`obj`}</inlineCode>{`, their internal properties remained equal. The `}<inlineCode parentName="p">{`pugson`}</inlineCode>{` object is shared between both.`}</p>
    <p>{`So if you're passing deep properties into your context or your props, your app is going to break.`}</p>
    <h2 {...{
      "id": "an-example-codesandbox"
    }}>{`An example CodeSandbox`}</h2>
    <p>{`Okay, so you're gonna need some state, a way to change it, and a way to communicate changes. Here's what that would look like in code 👇`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/101rzp1lpj",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p>{`You can say hi to Pugson. Type into the input box and see what you're typing right away.`}</p>
    <p>{`It's a little contrived, but that makes it easier to explain. There are a few moving pieces.`}</p>
    <h3 {...{
      "id": "1-the-context-itself"
    }}>{`1. The Context itself`}</h3>
    <p>{`I like to define the context itself in its own file. You could have a file with multiple context definitions for different parts of your app.`}</p>
    <p>{`This creates a convenient way for different components to share the same context via `}<inlineCode parentName="p">{`import`}</inlineCode>{` statements.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// GreetContext.js
import React from "react"

const { Provider, Consumer } = React.createContext()

export { Provider, Consumer }
`}</code></pre>
    <p>{`Create context and export its `}<inlineCode parentName="p">{`Provider`}</inlineCode>{` and `}<inlineCode parentName="p">{`Consumer`}</inlineCode>{`. Providers pass values down the tree of components, and consumers use them to do stuff.`}</p>
    <h3 {...{
      "id": "2-the-app-component-holds-and-provides-state"
    }}>{`2. The App component holds and provides state`}</h3>
    <p>{`Like I said, the simplest way to keep state that communicates changes is with component state. You should have a single source of truth for your entire app like always.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// App.js
class App extends React.Component {
  state = {
    greeting: "",
    setGreeting: ({ value }) => this.setState({ greeting: value }),
  }
  render() {
    return (
      <div class="App">
        <provider value={this.state}>
          <form>
            <greeting></greeting>
          </form>
        </provider>
      </div>
    )
  }
}
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`state`}</inlineCode>{` object has both values and setters. `}<inlineCode parentName="p">{`greeting`}</inlineCode>{` is the string you're typing with a default value of `}<inlineCode parentName="p">{`""`}</inlineCode>{`, and `}<inlineCode parentName="p">{`setGreeting`}</inlineCode>{` is the setter any component can use to change the greeting.`}</p>
    <p>{`Since we're using component state, the setter can call `}<inlineCode parentName="p">{`setState`}</inlineCode>{` and let React figure out the rest.`}</p>
    <p>{`My favorite side-effect of this approach is that important parts of your code are close together. You can start thinking of your state as a `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/State_machine"
      }}>{`state machine`}</a>{` because states and their transitions are next to each other.`}</p>
    <p>{`As your state grows in complexity, you might want to move this machinery out of `}<inlineCode parentName="p">{`App.js`}</inlineCode>{` into its own file. Just make sure your App component knows when something changes.`}</p>
    <p>{`When rendering we use `}<inlineCode parentName="p">{`Provide`}</inlineCode>{` to pass this component state as a context value down to our entire component tree.`}</p>
    <h3 {...{
      "id": "3-consuming-context-state"
    }}>{`3. Consuming context state`}</h3>
    <p>{`Consuming your state is a matter of rendering a \`\`and using the values it provides.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Greeting.js
import React from "react"

import { Consumer } from "./GreetContext"

export default () => (
  <consumer>
    {({ greeting }) => (
      <div>
        <h3>Your greeting 👋</h3>
        {greeting}
      </div>
    )}
  </consumer>
)
`}</code></pre>
    <p>{`See how keeping the context definition in a separate file makes it more convenient to use?`}</p>
    <p>{`We import the `}<inlineCode parentName="p">{`Consumer`}</inlineCode>{`, render it as the root of our `}<inlineCode parentName="p">{`Greeting`}</inlineCode>{` component, and pass-in a function as children. The good old render props approach but with children.`}</p>
    <p>{`Since our context value is an object, we can destructure it right away and take out just what we need: `}<inlineCode parentName="p">{`greeting`}</inlineCode>{`. Then render as usual.`}</p>
    <p>{`Whenever the `}<inlineCode parentName="p">{`greeting`}</inlineCode>{` value changes, our component will automatically re-render and show the new value.`}</p>
    <h3 {...{
      "id": "4-changing-context-state"
    }}>{`4. Changing context state`}</h3>
    <p>{`Changing our `}<inlineCode parentName="p">{`greeting`}</inlineCode>{` value works in a similar way. We render a consumer, take out the setter, and pass it as a prop into a component that does the changes.`}</p>
    <blockquote>
      <p parentName="blockquote">{`But there’s no way to access it outside of render, is there?`}</p>
      <p parentName="blockquote">{`@swizec waiting for your expert answer. Maybe I’ve been doing unstated wrong the whole time`}</p>
    </blockquote>
    <p>{`This also lets you solve the problem that you can't access context value outside of the render method. It's true, you cannot, and that's why you pass them into child components and use it there.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Form.js
import React from "react";

import { Consumer } from "./GreetContext";

const Input = ({ value, onChange }) => (
  <input value={value} onchange="{event" ==""> onChange({ value: event.target.value })}
    style={{ width: "100%", fontSize: "1.5em" }}
  />
);

export default () => (
  <consumer>
    {({ greeting, setGreeting }) => (
      <div>
        <h1>Say hi to Pugson</h1>
        <img src="./img/profile_images-834049142515187713-cOtVTgLm_400x400.jpg" style={{ height: "60px" }}>
        <input value={greeting} onchange={setGreeting}>
      </div>
    )}
  </consumer>
);
`}</code></pre>
    <p>{`Once more, we import that same shared context `}<inlineCode parentName="p">{`Consumer`}</inlineCode>{`, render, and use a function as children approach. This time, we take both the `}<inlineCode parentName="p">{`greeting`}</inlineCode>{` and `}<inlineCode parentName="p">{`setGreeting`}</inlineCode>{` out of our context value.`}</p>
    <p>{`We pass those into the `}<inlineCode parentName="p">{`Input`}</inlineCode>{` component as `}<inlineCode parentName="p">{`value`}</inlineCode>{` and the `}<inlineCode parentName="p">{`onChange`}</inlineCode>{` callback. This allows `}<inlineCode parentName="p">{`Input`}</inlineCode>{` to be as complex or as simple as it wants, makes it a fully controlled component, and most importantly, `}<em parentName="p">{`it doesn't rely on context.`}</em></p>
    <p>{`A common foot-gun is to make your components so tied to context or state management that you can't reuse them. Always a good idea to make your basic components rely on props alone.`}</p>
    <h2 {...{
      "id": "some-modern-context-based-libraries"
    }}>{`Some modern context-based libraries`}</h2>
    <p><em parentName="p">{`"But that's an awful lot like re-inventing your own state management"`}</em>{`, I hear you say.`}</p>
    <p>{`Kind of? You're using out-of-the-box React tools. No wheel reinventing required.`}</p>
    <p>{`But yes, as complexity grows, you start moving this machinery out of your main component into its own files, and start running into a lot of similar problems as you would with building your own state management library.`}</p>
    <p>{`When that happens, I recommend splitting your context into subcontexts. Have a new context with a new state object for every section of your app.`}</p>
    <p>{`A form could have its own state and context, for example, use it to communicate between all the fields, then communicate its end result to the parent context. Much like nested redux reducers or something.`}</p>
    <p>{`You can also make some of this stuff easier with modern state management libraries like `}<a parentName="p" {...{
        "href": "https://github.com/diegohaz/constate"
      }}>{`Constate`}</a>{` or `}<a parentName="p" {...{
        "href": "https://github.com/jamiebuilds/unstated"
      }}>{`Unstated`}</a>{`.`}</p>
    <p>{`I won't go into detail on those, so here's two videos:`}</p>
    <lite-youtube {...{
      "videoid": "63UI2nTz1qA",
      "videostartat": "0"
    }}></lite-youtube>
    <lite-youtube {...{
      "videoid": "MhmhfaJn0_U",
      "videostartat": "0"
    }}></lite-youtube>
    <h2 {...{
      "id": "what-about-hooks"
    }}>{`What about hooks?`}</h2>
    <p>{`Yes, this approach works with hooks. Replace \``}<inlineCode parentName="p">{`with `}</inlineCode>{`useContext\`. Everything else stays the same.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import GreetingContext from "./GreetingContext"
export default () => {
  const { greeting } = useContext(GreetingContext)

  return (
    <div>
      <h3>Your greeting 👋</h3>
      {greeting}
    </div>
  )
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      